import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  fetchRequirementSteps,
  fetchStepActions
} from '../../services/RequirementStepsService';
import {
  RequirementStep,
  RequirementStepsResponse,
  StepAction,
  StepDetailsResponse
} from '../../types/RequirementSteps.types';

// Async thunk for fetching requirement steps
export const fetchRequirementStepsByAISystemId = createAsyncThunk(
  'requirementSteps/fetchByAISystemId',
  async (aiSystemId: string, { rejectWithValue }) => {
    try {
      const response = await fetchRequirementSteps(aiSystemId);
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.message || 'Failed to fetch requirement steps'
      );
    }
  }
);

// Async thunk for fetching step actions
export const fetchStepActionsByStepId = createAsyncThunk(
  'requirementSteps/fetchActionsByStepId',
  async (stepId: string, { rejectWithValue }) => {
    try {
      const response = await fetchStepActions(stepId);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch step actions');
    }
  }
);

type RequirementStepsState = {
  steps: RequirementStep[];
  aiSystemId: string | null;
  loading: boolean;
  error: string | null;
  // Step actions state
  currentStepActions: StepAction[];
  currentStepId: string | null;
  actionsLoading: boolean;
  actionsError: string | null;
  // indicator for models
  fromRequirement: boolean;
};

const initialState: RequirementStepsState = {
  steps: [],
  aiSystemId: null,
  loading: false,
  error: null,
  currentStepActions: [],
  currentStepId: null,
  actionsLoading: false,
  actionsError: null,
  fromRequirement: false
};

export const requirementStepsSlice = createSlice({
  name: 'requirementSteps',
  initialState,
  reducers: {
    clearRequirementSteps: (state) => {
      state.steps = [];
      state.aiSystemId = null;
      state.error = null;
    },
    clearError: (state) => {
      state.error = null;
    },
    clearStepActions: (state) => {
      state.currentStepActions = [];
      state.currentStepId = null;
      state.actionsError = null;
    },
    clearActionsError: (state) => {
      state.actionsError = null;
    },
    setFromRequirement: (state, action: PayloadAction<boolean>) => {
      state.fromRequirement = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch requirement steps
      .addCase(fetchRequirementStepsByAISystemId.pending, (state, action) => {
        const isRefetchForCurrentSystem = state.aiSystemId === action.meta.arg;
        state.loading = state.steps.length === 0 || !isRefetchForCurrentSystem;
        state.aiSystemId = action.meta.arg;
        state.error = null;
      })
      .addCase(
        fetchRequirementStepsByAISystemId.fulfilled,
        (state, action: PayloadAction<RequirementStepsResponse>) => {
          state.loading = false;
          state.steps = action.payload.steps;
          state.aiSystemId = action.payload.aiSystemId || state.aiSystemId;
          state.error = null;
        }
      )
      .addCase(fetchRequirementStepsByAISystemId.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch step actions
      .addCase(fetchStepActionsByStepId.pending, (state) => {
        state.actionsLoading = true;
        state.actionsError = null;
      })
      .addCase(
        fetchStepActionsByStepId.fulfilled,
        (state, action: PayloadAction<StepDetailsResponse>) => {
          state.actionsLoading = false;
          state.currentStepActions = action.payload.actions;
          state.currentStepId = action.payload.stepId;
          state.actionsError = null;
        }
      )
      .addCase(fetchStepActionsByStepId.rejected, (state, action) => {
        state.actionsLoading = false;
        state.actionsError = action.payload as string;
      });
  }
});

export const {
  clearRequirementSteps,
  clearError,
  clearStepActions,
  clearActionsError,
  setFromRequirement
} = requirementStepsSlice.actions;

export default requirementStepsSlice.reducer;
